import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import Api from './utils/apiCall';
import { API_URLS } from './apis/urls';

interface AwsBucketState {
  loading: boolean;
  error: string | null;
  presignedUrl: string | null;
}

const initialState: AwsBucketState = {
  loading: false,
  error: null,
  presignedUrl: null,
};

// Async thunk to get presigned URL
export const getPresignedUrl = createAsyncThunk(
  'awsBucket/getPresignedUrl',
  async (key: string, { rejectWithValue }) => {
    try {
      const response = await Api.post(API_URLS.AWS_BUCKET.GET_PRESIGNED_URL, {
        key,
      });

      const resData = response.data;
      let url = "";

      if (typeof resData === 'string') {
        url = resData;
      } else if (resData?.data && typeof resData.data === 'string') {
        url = resData.data;
      } else if (resData?.data?.url && typeof resData.data.url === 'string') {
        url = resData.data.url;
      } else if (resData?.url && typeof resData.url === 'string') {
        url = resData.url;
      }

      if (url) {
        return url;
      }

      throw new Error('Invalid response format');
    } catch (error: any) {
      return rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

// Async thunk to upload file
export const uploadFile = createAsyncThunk(
  'awsBucket/uploadFile',
  async (requestBody: { filestring: string, mime_type: string, module: string }, { rejectWithValue }) => {
    try {
      const response = await Api.post(API_URLS.AWS_BUCKET.UPLOAD_FILE, requestBody);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

const awsBucketSlice = createSlice({
  name: 'awsBucket',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearPresignedUrl: (state) => {
      state.presignedUrl = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get presigned URL
      .addCase(getPresignedUrl.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.presignedUrl = null;
      })
      .addCase(getPresignedUrl.fulfilled, (state, action) => {
        state.loading = false;
        state.presignedUrl = action.payload;
      })
      .addCase(getPresignedUrl.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'Failed to get presigned URL';
      })
      // Upload file
      .addCase(uploadFile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(uploadFile.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(uploadFile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'Failed to upload file';
      });
  },
});

export const { clearError, clearPresignedUrl } = awsBucketSlice.actions;
export default awsBucketSlice.reducer;
